import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Image,
  FileText,
  Eye,
  EyeOff,
  MessageSquare,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    published: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/articles`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, use a placeholder or direct URL
    // In production, you'd upload to a storage service
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    setUploading(true);
    try {
      // Use the video upload endpoint for images too (it accepts any file)
      // Or create a dedicated image upload endpoint
      const reader = new FileReader();
      reader.onloadend = () => {
        // For simplicity, we'll ask user to provide URL
        // You can implement proper image upload later
        setMessage({ type: 'info', text: 'Introdu URL-ul imaginii direct sau încarcă pe un serviciu de imagini (ex: imgur.com)' });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la încărcarea imaginii' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/articles/${editingId}`, formData);
        setMessage({ type: 'success', text: 'Articol actualizat!' });
      } else {
        await axios.post(`${API_URL}/api/admin/articles`, formData);
        setMessage({ type: 'success', text: 'Articol creat!' });
      }
      
      fetchArticles();
      resetForm();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea articolului' });
    }
  };

  const handleEdit = (article) => {
    setFormData({
      title: article.title,
      content: article.content,
      image_url: article.image_url || '',
      published: article.published
    });
    setEditingId(article.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigură că vrei să ștergi acest articol?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/articles/${id}`);
      setMessage({ type: 'success', text: 'Articol șters!' });
      fetchArticles();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la ștergerea articolului' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', image_url: '', published: true });
    setEditingId(null);
    setShowForm(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Jurnal</h1>
          <p className="text-slate-400 mt-1">Scrie articole și împărtășește experiențele tale</p>
        </div>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Articol Nou
          </Button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : message.type === 'error'
            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
            : 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
        }`}>
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl p-6 border border-slate-700 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-400" />
              {editingId ? 'Editează Articol' : 'Articol Nou'}
            </h2>
            <button type="button" onClick={resetForm} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Titlu</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              placeholder="Titlul articolului..."
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Conținut</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              required
              rows={10}
              placeholder="Scrie aici articolul tău... Poți folosi paragrafe separate cu Enter."
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              <Image className="w-4 h-4 inline mr-1" />
              URL Imagine
            </label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://exemple.com/imagine.jpg"
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
            />
            {formData.image_url && (
              <div className="mt-2">
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  className="w-32 h-32 object-cover rounded-xl"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Sfat: Poți folosi imgur.com sau unsplash.com pentru a găsi/încărca imagini
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData({...formData, published: e.target.checked})}
              className="w-4 h-4 rounded bg-slate-700 border-slate-600"
            />
            <label htmlFor="published" className="text-sm text-slate-300">
              Publicat (vizibil pe site)
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl">
              <Save className="w-4 h-4 mr-2" />
              {editingId ? 'Salvează Modificările' : 'Publică Articolul'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">
              Anulează
            </Button>
          </div>
        </form>
      )}

      {/* Articles List */}
      <div className="space-y-4">
        {articles.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 text-center border border-slate-700">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Nu ai articole încă</h3>
            <p className="text-slate-400">Scrie primul tău articol pentru a împărtăși experiențele tale!</p>
          </div>
        ) : (
          articles.map(article => (
            <div 
              key={article.id} 
              className="bg-slate-800 rounded-2xl p-4 border border-slate-700 flex gap-4"
            >
              {/* Image */}
              {article.image_url ? (
                <img 
                  src={article.image_url} 
                  alt={article.title}
                  className="w-32 h-32 object-cover rounded-xl flex-shrink-0"
                />
              ) : (
                <div className="w-32 h-32 bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-slate-500" />
                </div>
              )}
              
              {/* Content */}
              <div className="flex-grow min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-white truncate">{article.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.created_at)}
                      </span>
                      <span className={`flex items-center gap-1 ${article.published ? 'text-green-400' : 'text-yellow-400'}`}>
                        {article.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {article.published ? 'Publicat' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(article)}
                      className="p-2 text-slate-400 hover:text-sky-400 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mt-2 line-clamp-2">
                  {article.content}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminArticles;
